import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ShieldCheck, Mail, AtSign, Bell, BellOff, BellRing } from "lucide-react";
import { isPushSupported, getPushStatus, subscribePush, unsubscribePush, sendTestPush } from "@/lib/push";

export default function AdminAccount() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-account-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--dd-green)]">My account</h1>
        <p className="text-sm text-[var(--dsf-text-secondary)] mt-1">
          Update the credentials used to sign into the admin / manager dashboard.
        </p>
        {me && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-[var(--dsf-text-muted)] bg-[var(--dd-bone)] px-3 py-1.5 rounded-full">
            <AtSign size={12} /> Currently signed in as <strong className="text-[var(--dd-green)]">{me.email}</strong>
          </div>
        )}
      </div>

      <ChangeEmailCard currentEmail={me?.email} onChanged={(e) => setMe((m) => ({ ...m, email: e }))} />
      <PushNotificationsCard />
      <ChangePasswordCard />
      {me?.role === "admin" && <SnapshotRestoreCard />}
    </div>
  );
}

/* ============== Restore snapshot card (admin only) ============== */
function SnapshotRestoreCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    api.get("/admin/snapshot/info").then((r) => setInfo(r.data)).catch(() => setInfo({ available: false }));
  }, []);

  const restore = async () => {
    if (!confirm("Replace ALL products, content, team, sides, etc. with the bundled snapshot? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/snapshot/restore", { confirm: true });
      setRestored(r.data.restored);
      toast.success("Snapshot restored — your CMS data now matches the preview environment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
      setShowConfirm(false);
    }
  };

  if (!info) return null;

  return (
    <div className="bg-white rounded-2xl border border-[var(--dsf-border)] p-6 md:p-8 space-y-4" data-testid="snapshot-restore-card">
      <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold">
        📦 Production data sync
      </div>
      <p className="text-sm text-[var(--dsf-text-secondary)]">
        Replace the products, side dishes, team, testimonials, articles, FAQ, and homepage content of <strong>this environment</strong> with a snapshot taken from the preview build. <strong>Run once after deploying</strong> to align production with what you&apos;ve been configuring in preview.
      </p>

      {!info.available ? (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
          ⚠️ No snapshot bundled with this build.
        </div>
      ) : (
        <>
          <div className="rounded-xl bg-[var(--dd-bone)] p-4 text-xs space-y-1.5">
            <div className="font-semibold text-[var(--dd-green)] text-sm mb-2">Snapshot contents</div>
            {Object.entries(info.collections || {}).map(([col, n]) => (
              <div key={col} className="flex justify-between" data-testid={`snapshot-info-${col}`}>
                <span className="capitalize text-[var(--dsf-text-secondary)]">{col.replace("_", " ")}</span>
                <span className="font-mono text-[var(--dd-green)]">{n} docs</span>
              </div>
            ))}
            <div className="border-t border-black/5 pt-1.5 mt-1.5 flex justify-between font-semibold">
              <span className="text-[var(--dd-green)]">Total</span>
              <span className="font-mono text-[var(--dd-green)]">{info.total_docs} docs</span>
            </div>
            {info.exported_at && (
              <div className="text-[10px] text-[var(--dsf-text-muted)] pt-1.5">Exported: {new Date(info.exported_at).toLocaleString()}</div>
            )}
          </div>

          {!restored ? (
            <button
              onClick={restore}
              disabled={busy}
              className="bg-[var(--dd-mustard)] text-[var(--dd-green)] font-bold px-5 py-3 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm uppercase tracking-wider flex items-center gap-2"
              data-testid="snapshot-restore-button"
            >
              {busy ? "Restoring…" : "📥 Restore snapshot now"}
            </button>
          ) : (
            <div className="rounded-xl bg-emerald-50 border border-emerald-300 p-4 text-sm text-emerald-900 space-y-1" data-testid="snapshot-restored-summary">
              <div className="font-semibold">✅ Snapshot restored</div>
              {Object.entries(restored).map(([col, r]) => (
                <div key={col} className="text-xs flex justify-between">
                  <span className="capitalize">{col}</span>
                  <span className="font-mono">{r.deleted} deleted · {r.inserted} inserted</span>
                </div>
              ))}
            </div>
          )}

          <div className="text-[11px] text-[var(--dsf-text-muted)] pt-2 border-t border-[var(--dsf-border)]">
            ⚠️ Orders, customers, payments, and admin accounts are <strong>never</strong> touched by this operation — only CMS data is replaced.
          </div>
        </>
      )}
    </div>
  );
}

/* ============== Push Notifications card ============== */
function PushNotificationsCard() {
  const [status, setStatus] = useState("loading"); // loading | unsupported | denied | not_subscribed | subscribed
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const supported = await isPushSupported();
      if (!supported) return setStatus("unsupported");
      setStatus(await getPushStatus());
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifications enabled — you'll be alerted on this device for every new order");
      setStatus("subscribed");
    } catch (e) {
      toast.error(e.message || "Failed to enable notifications");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Notifications disabled on this device");
      setStatus("not_subscribed");
    } catch (e) {
      toast.error(e.message || "Failed to disable notifications");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Test sent — you should receive a notification within a few seconds.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Test failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[var(--dsf-border)] p-6 md:p-8 space-y-4" data-testid="push-notifications-card">
      <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold">
        <BellRing size={16} /> Push notifications
      </div>
      <p className="text-sm text-[var(--dsf-text-secondary)]">
        Get a system-level notification on this device whenever a new order arrives — even if the dashboard tab is closed or your screen is locked.
      </p>

      {status === "loading" && <div className="text-sm text-[var(--dsf-text-muted)]">Checking status…</div>}

      {status === "unsupported" && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900">
          ⚠️ Your browser does not support push notifications. Use Chrome, Edge, Firefox, or Safari (16.4+).
        </div>
      )}

      {status === "denied" && (
        <div className="rounded-xl bg-red-50 border border-red-300 p-3 text-sm text-red-900">
          🚫 Notifications are <strong>blocked</strong> by your browser. Open site permissions and allow notifications, then reload this page.
        </div>
      )}

      {status === "not_subscribed" && (
        <button onClick={enable} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="push-enable-button">
          <Bell size={15} /> {busy ? "Enabling…" : "Enable push notifications on this device"}
        </button>
      )}

      {status === "subscribed" && (
        <div className="space-y-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900 flex items-center gap-2">
            <Bell size={14} /> Push notifications are <strong>active</strong> on this device.
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={test} disabled={busy} className="btn-mustard !py-2 disabled:opacity-50" data-testid="push-test-button">
              <BellRing size={14} /> Send test notification
            </button>
            <button onClick={disable} disabled={busy} className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--dsf-border)] hover:bg-red-50 text-[var(--dd-coral)]" data-testid="push-disable-button">
              <BellOff size={14} /> Disable on this device
            </button>
          </div>
        </div>
      )}

      <div className="text-[11px] text-[var(--dsf-text-muted)] pt-2 border-t border-[var(--dsf-border)] space-y-1">
        <div>📱 <strong>iPhone</strong> : install the dashboard as a PWA first (Safari → Share → &quot;Add to Home Screen&quot;), then enable notifications from the installed app.</div>
        <div>💻 <strong>PC/Mac</strong> : works in any Chromium/Firefox/Safari tab — even when the tab is in background.</div>
      </div>
    </div>
  );
}

/* ============== Email card ============== */
function ChangeEmailCard({ currentEmail, onChanged }) {
  const [form, setForm] = useState({ password: "", newEmail: "" });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.newEmail)) return toast.error("Invalid email format");
    if (form.newEmail.toLowerCase() === (currentEmail || "").toLowerCase()) return toast.error("Same as current email");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/change-email", { current_password: form.password, new_email: form.newEmail });
      toast.success(data.message || "Email updated", { duration: 6000 });
      onChanged?.(data.email || form.newEmail);
      setForm({ password: "", newEmail: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--dsf-border)] p-6 md:p-8 space-y-5" data-testid="change-email-form">
      <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold">
        <Mail size={16} /> Change email
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-[var(--dsf-text-secondary)] font-bold block mb-2">New email</label>
        <input
          type="email" required value={form.newEmail}
          onChange={(e) => setForm({ ...form, newEmail: e.target.value })}
          placeholder="new@email.com"
          className="w-full border border-black/15 rounded-xl px-4 py-3 text-sm focus:border-[var(--dd-green)] outline-none"
          data-testid="email-new"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-[var(--dsf-text-secondary)] font-bold block mb-2">Confirm with current password</label>
        <div className="relative">
          <input
            type={show ? "text" : "password"} required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="current-password"
            className="w-full border border-black/15 rounded-xl px-4 py-3 pr-11 text-sm focus:border-[var(--dd-green)] outline-none"
            data-testid="email-password"
          />
          <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--dsf-text-muted)] hover:text-[var(--dd-green)]">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <button type="submit" disabled={submitting || !form.password || !form.newEmail}
        className="btn-primary disabled:opacity-50" data-testid="submit-change-email">
        <Mail size={15} /> {submitting ? "Updating…" : "Update email"}
      </button>
    </form>
  );
}

/* ============== Password card ============== */
function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const strength = scorePassword(form.next);
  const strengthLabel = ["Too weak", "Weak", "Fair", "Strong", "Very strong"][strength] || "Too weak";
  const strengthClass = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"][strength] || "bg-red-500";

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.next !== form.confirm) return toast.error("Passwords do not match");
    if (form.next === form.current) return toast.error("New password must differ from current");
    setSubmitting(true);
    try {
      await api.post("/admin/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated. Use it next time you log in.", { duration: 6000 });
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--dsf-border)] p-6 md:p-8 space-y-5" data-testid="change-password-form">
      <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold">
        <Lock size={16} /> Change password
      </div>

      <PasswordField label="Current password" value={form.current} show={showCurrent} onToggle={() => setShowCurrent((v) => !v)}
        onChange={(v) => setForm({ ...form, current: v })} testid="current-password" />
      <PasswordField label="New password" value={form.next} show={showNext} onToggle={() => setShowNext((v) => !v)}
        onChange={(v) => setForm({ ...form, next: v })} testid="new-password" />

      {form.next && (
        <div className="space-y-1.5" data-testid="strength-meter">
          <div className="flex gap-1 h-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`flex-1 rounded-full ${i < strength ? strengthClass : "bg-gray-200"} transition-colors`} />
            ))}
          </div>
          <div className="text-xs text-[var(--dsf-text-muted)] flex justify-between">
            <span>Strength: <span className="font-semibold">{strengthLabel}</span></span>
            <span>Min 8 characters</span>
          </div>
        </div>
      )}

      <PasswordField label="Confirm new password" value={form.confirm} show={showNext} onToggle={() => setShowNext((v) => !v)}
        onChange={(v) => setForm({ ...form, confirm: v })} testid="confirm-password" mismatch={form.confirm && form.confirm !== form.next} />

      <button type="submit" disabled={submitting || !form.current || !form.next || !form.confirm}
        className="btn-primary disabled:opacity-50" data-testid="submit-change-password">
        <ShieldCheck size={15} /> {submitting ? "Updating…" : "Update password"}
      </button>

      <div className="text-[11px] text-[var(--dsf-text-muted)] pt-2 border-t border-[var(--dsf-border)]">
        🔒 Hashed with bcrypt. Changes apply immediately and persist across server restarts.
      </div>
    </form>
  );
}

function PasswordField({ label, value, show, onToggle, onChange, testid, mismatch }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[var(--dsf-text-secondary)] font-bold block mb-2">{label}</label>
      <div className="relative">
        <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
          autoComplete={testid === "current-password" ? "current-password" : "new-password"}
          className={`w-full border ${mismatch ? "border-red-400" : "border-black/15"} rounded-xl px-4 py-3 pr-11 text-sm focus:border-[var(--dd-green)] outline-none`}
          data-testid={testid} />
        <button type="button" tabIndex={-1} onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--dsf-text-muted)] hover:text-[var(--dd-green)]">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {mismatch && <div className="text-xs text-red-600 mt-1">Passwords don&apos;t match</div>}
    </div>
  );
}

function scorePassword(pwd) {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 8) s++;
  if (pwd.length >= 12) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) s++;
  return Math.min(s, 4);
}
